import React, { useCallback, useEffect, useRef,useState } from 'react';

import PropTypes from 'prop-types';
import cx from 'classnames';
import CSSModule from 'react-css-modules';
import { FormattedMessage, injectIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { Edit2, Grid, List, Eye, Heart, Trash2, CheckCircle, RefreshCw, Bell } from 'react-feather';
import { connect, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useNavigate } from 'react-router-dom';
import { actions as businessActions } from 'coupon-common/src/modules/businessInfo';
import { AppWrapper, Coupon, Feedback, Spinner } from 'components';
import { AuthProtected, CouponDelete, CouponRepublish } from 'containers';
import SendCouponNotificationModal from 'components/SendCouponNotificationModal/SendCouponNotificationModal';
import { products } from 'containers/CouponBuilderV1/utils/sendNotification';
import { COUPON_STATES } from 'coupon-common';
import { actions, duck } from 'coupon-common/src/modules/coupons';
import { actions as followersActions } from 'coupon-common/src/modules/followers';
import { dispatchAsAsync, getNormalizedEntity } from 'coupon-common/src/utils';
import { useNextPageHandler } from 'hooks';
import { FORM_ERROR } from 'final-form';

// Reused as-is from MyBusinessAccount.js — same hero card (cover photo,
// logo, business name, email) already wired up against businessInfo there,
// so the "who is this business" identity block looks identical wherever it
// appears instead of being redrawn from scratch here.
import BusinessCard from '../Dashboard/Components/BusinessCard';
import '../Dashboard/Components/dashboard.responsive.css';
import BusinessInfoEditModal from '../../components/BusinessInfoEditModal/BusinessInfoEditModal';

import messages from './messages';
import styles from './MyCoupons.module.scss';

const DEFAULT_TRANSITION_TIMEOUT = 300;
const DEFAULT_NEXT_PAGE_LOADING_SKELETON_HEIGHT = 50;
const DEFAULT_NEXT_PAGE_LOADING_SKELETON_DURATION = 0.9;

const couponsEntityKey = duck.couponsEntity.key;

// city/province come back from the API as lookup objects ({ id, name,
// abbreviation }), not plain strings — this pulls a displayable label out
// of either shape so they're safe to render directly.
const getLocationLabel = value => {
  if (!value) return '';
  if (typeof value === 'object') return value.name || value.abbreviation || '';
  return String(value);
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: COUPON_STATES.PUBLISHED, label: 'Published' },
  { value: COUPON_STATES.DRAFT, label: 'Draft' },
  { value: COUPON_STATES.EXPIRED, label: 'Expired' },
];

// Maps each analytics stat to its icon — swapped in for the old "Views" /
// "Likes" text labels. The message id is still used for the accessible
// title/aria-label either way.
const ANALYTICS_ICONS = {
  viewsText: Eye,
  likesText: Heart,
};

export const MyCoupons = ({ intl, dispatch, coupons, businessInfo, viewCounts, followersCount }) => {
  dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    
      dispatch(actions.getBusinessCoupons());
    
  }, [dispatch]);

  useEffect(() => {
    if (!followersCount) {
      dispatch(followersActions.getBusinessFollowers());
    }
  }, [dispatch]);

  const [couponActionModalOpen, setCouponActionModalOpen] = useState(false);
  const [republishCouponId, setRepublishCouponId] = useState(null);
  const [deleteCouponData, setDeleteCouponData] = useState(null);
  const [sendNotificationCouponData, setSendNotificationCouponData] = useState(null);

  // Tracks which grid card (by id) currently has its Views/Likes +
  // Edit/Delete row expanded. Clicking a card's image/media toggles it;
  // clicking a different card while one is open swaps to the new one
  // rather than stacking multiple open rows. List view ignores this
  // entirely — its actions sit inline in the card's footer, always
  // visible.
  const [expandedCouponId, setExpandedCouponId] = useState(null);
  const toggleExpandedCoupon = id => {
    setExpandedCouponId(current => (current === id ? null : id));
  };

  const openCouponActionModal = () => setCouponActionModalOpen(true);
  const closeCouponActionModal = () => {
    setCouponActionModalOpen(false);
    setRepublishCouponId(null);
    setDeleteCouponData(null);
    setSendNotificationCouponData(null);
  };

  // Business Info card's edit icon opens this instead of navigating to
  // /account — a tabbed modal (Info / Media / Social) right on this page.
  const [businessEditModalOpen, setBusinessEditModalOpen] = useState(false);
  const [businessInfoSaving, setBusinessInfoSaving] = useState(false);

  // WIRING NOTE: this is a placeholder — swap in the real business-update
  // dispatch/API call here (and any upload step for logo/banner File
  // objects) once that action is available. Left as a stub so the modal
  // is otherwise fully functional.
const handleBusinessInfoSave = async formValues => {
  setBusinessInfoSaving(true);
  try {
    const payload = {
      ...businessInfo,
      name: formValues.businessName,
      contact_name: formValues.contactName,
      phone: formValues.phone,
      email: formValues.email,
      website: formValues.website,
      street: formValues.streetAddress, // see street/address note below
      city: formValues.city,
      province: formValues.province,
      postal_code: formValues.postalCode,
      country: formValues.country,
      terms_and_conditions: formValues.termsAndConditions,
      logo: formValues.logo,       // real File, not the base64 preview — see prior note
      cover_image: formValues.banner,
    };

    const result = await dispatchAsAsync(businessActions.updateInfo(payload), dispatch);

    if (!result) {
      setBusinessEditModalOpen(false);
    } else if (result[FORM_ERROR]) {
      // surface result[FORM_ERROR] the same way MyBusinessAccount.js does
    } else {
      // surface field errors the same way MyBusinessAccount.js does
    }
  } catch (e) {
    // surface a generic failure message
  } finally {
    setBusinessInfoSaving(false);
  }
};




  const {
    entityList: couponsList,
    isFetching: isCouponsFetching,
    error: couponsFetchingError,
    isNextPageExist: couponsNextPageExist,
  } = React.useMemo(
    () => getNormalizedEntity(coupons, couponsEntityKey, intl, messages.couponsEntity),
    [coupons],
  );
  const isCouponsActionsFetching = coupons.couponsActions.fetching;
  const couponsActionsError = coupons.couponsActions.error;
  const verifiedBusiness = businessInfo?.verified;

    const [hasLoadedCoupons, setHasLoadedCoupons] = useState(false);
    useEffect(() => {
      if (couponsList.length > 0) {
        setHasLoadedCoupons(true);
      }
    }, [couponsList]);

  // True once the first coupon fetch for this visit has finished. Until then
  // the page shows a spinner instead of stale (or empty) store data. This
  // matters after publish/save-draft navigates back here with the previous
  // list still sitting in the store.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const sawCouponsFetching = useRef(false);
  useEffect(() => {
    if (isCouponsFetching) {
      sawCouponsFetching.current = true;
    } else if (sawCouponsFetching.current) {
      setInitialLoadDone(true);
    }
  }, [isCouponsFetching]);

  const hasFetchedViewCounts = useRef(false);
  useEffect(() => {
    if (couponsList.length > 0 && !hasFetchedViewCounts.current) {
      hasFetchedViewCounts.current = true;
    }
  }, [couponsList]);

  useNextPageHandler(
    actions.getBusinessCouponsNextPage,
    couponsNextPageExist,
    isCouponsFetching,
    { isCouponsActionsNotFetching: !isCouponsActionsFetching },
  );

  const handleCouponActionBtnClick = async (couponId, couponTitle, action, notificationMessageId) => {
    try {
      const result = await dispatchAsAsync(action(couponId), dispatch);
      const failed = result && (result.success === false || result[FORM_ERROR]);
      if (failed) {
        toast.error(result[FORM_ERROR] || 'Something went wrong. Please try again.');
        return;
      }
      toast(
        <FormattedMessage
          {...notificationMessageId}
          values={{ couponTitle: <b>{couponTitle}</b> }}
        />,
      );
      dispatch(actions.getBusinessCoupons());
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const sendNotificationPaymentHandler = () => {
    closeCouponActionModal();
  };

  // Grid view's dropdown buttons — same stroke style as the list view's
  // pill CTAs (#3c3c3c border, white fill, react-feather vector icons)
  // but icon-only, no label text, since the dropdown is compact.
  const renderGridActionButton = ({ key, Icon, label, onClick, disabled, variant, disabledTitle }) => (
    <button
      type="button"
      styleName={cx('grid-cta-btn', { 'grid-cta-delete': variant === 'delete' })}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={disabled && disabledTitle ? disabledTitle : label}
      key={key}
    >
      <Icon size={16} color="#3c3c3c" />
    </button>
  );

  const renderPublishCouponButton = (itemId, couponTitle) => {
    const label = intl.formatMessage(messages.publishCouponButtonLabel);
    return renderGridActionButton({
      key: `${itemId}/${label}`,
      Icon: CheckCircle,
      label,
      onClick: () => handleCouponActionBtnClick(itemId, couponTitle, actions.publishCoupon, messages.publishCouponSuccessText),
      disabled: isCouponsActionsFetching || !verifiedBusiness,
      disabledTitle: 'Your business must be verified before you can publish.',
    });
  };

  const renderEditCouponButton = itemId => {
    const label = intl.formatMessage(messages.editCouponButtonLabel);
    return renderGridActionButton({
      key: `${itemId}/${label}`,
      Icon: Edit2,
      label,
      onClick: () => navigate(`/coupons/${itemId}?mode=edit&step=5`),
      disabled: isCouponsActionsFetching,
    });
  };

  const renderDeleteCouponButton = (itemId, couponTitle) => {
    const label = intl.formatMessage(messages.deleteCouponButtonLabel);
    return renderGridActionButton({
      key: `${itemId}/${label}`,
      Icon: Trash2,
      label,
      onClick: () => {
        openCouponActionModal();
        setDeleteCouponData({ id: itemId, title: couponTitle });
      },
      disabled: isCouponsActionsFetching,
      variant: 'delete',
    });
  };

  const renderSendNotificationCouponButton = (itemId, couponTitle, coupon) => {
    if (followersCount === 0) return null;
    const label = intl.formatMessage(messages.sendNotificationsCouponButtonLabel);
    const isDisabled = !coupon?.notification_available || isCouponsActionsFetching;
    const disabledTitle = !coupon?.notification_available
      ? intl.formatMessage(messages.sendNotificationsTooltipText)
      : undefined;
    return renderGridActionButton({
      key: `${itemId}/${label}`,
      Icon: Bell,
      label,
      onClick: () => {
        openCouponActionModal();
        setSendNotificationCouponData({ couponId: itemId, coupon, businessInfo });
      },
      disabled: isDisabled,
      disabledTitle,
    });
  };

  // Icon replaces the old text label ("Views" / "Likes"). The message id
  // is kept and used as the pill's title/aria-label so the meaning is
  // still conveyed to screen readers and on hover, even though it's no
  // longer painted on screen.
  const renderAnalyticsData = useCallback((data, message_id) => {
    const AnalyticsIcon = ANALYTICS_ICONS[message_id];
    const label = intl.formatMessage(messages[message_id]);
    return (
      <div styleName="analytics-pill" title={label} aria-label={label}>
        <span styleName="analytics-value">{data || 0}</span>
        {AnalyticsIcon && <AnalyticsIcon styleName="analytics-icon" size={14} aria-hidden="true" />}
      </div>
    );
  }, [intl]);

  const renderRepublishCouponButton = itemId => {
    const label = intl.formatMessage(messages.republishCouponButtonLabel);
    return renderGridActionButton({
      key: `${itemId}/${label}`,
      Icon: RefreshCw,
      label,
      onClick: () => {
        setRepublishCouponId(itemId);
        openCouponActionModal();
      },
      disabled: isCouponsActionsFetching,
    });
  };

  // Sponsor removed entirely — published coupons now just get Edit + Delete
  // + Send Notification.
  const couponStateToActionsMap = {
    sponsored: [renderDeleteCouponButton],
    published: [renderEditCouponButton, renderDeleteCouponButton, renderSendNotificationCouponButton],
    draft: [renderPublishCouponButton, renderEditCouponButton, renderDeleteCouponButton],
    expired: [renderRepublishCouponButton, renderDeleteCouponButton],
    blocked: [renderDeleteCouponButton],
  };

  const getCouponActionButtons = item => {
    switch (true) {
      case item.is_blocked:
        return couponStateToActionsMap.blocked;
      case item.is_sponsored:
        return couponStateToActionsMap.sponsored;
      case item.state === COUPON_STATES.PUBLISHED:
        return couponStateToActionsMap.published;
      case item.state === COUPON_STATES.DRAFT:
        return couponStateToActionsMap.draft;
      case item.state === COUPON_STATES.EXPIRED:
        return couponStateToActionsMap.expired;
      default:
        return null;
    }
  };

  // ── List-view action buttons ────────────────────────────────────────
  // Grid view's dropdown (above) uses the same stroke-style vector icon
  // buttons, just icon-only with no label. List view adds the label text
  // and the gradient-free outline treatment matches the Redeem button's
  // shape/sizing from CustomerCouponCard, just recolored to #3c3c3c.
  const renderListActionButton = ({ key, Icon, label, onClick, disabled, variant, disabledTitle }) => (
    <button
      type="button"
      styleName={cx('list-cta-btn', { 'list-cta-delete': variant === 'delete' })}
      onClick={onClick}
      disabled={disabled}
      title={disabled && disabledTitle ? disabledTitle : undefined}
      key={key}
    >
      <Icon size={18} color="#3c3c3c" />
      {label}
    </button>
  );

  const renderListPublishButton = (itemId, couponTitle) => {
    const label = intl.formatMessage(messages.publishCouponButtonLabel);
    return renderListActionButton({
      key: `${itemId}/list-publish`,
      Icon: CheckCircle,
      label,
      onClick: () => handleCouponActionBtnClick(itemId, couponTitle, actions.publishCoupon, messages.publishCouponSuccessText),
      disabled: isCouponsActionsFetching || !verifiedBusiness,
      disabledTitle: 'Your business must be verified before you can publish.',
    });
  };

  const renderListEditButton = itemId => {
    const label = intl.formatMessage(messages.editCouponButtonLabel);
    return renderListActionButton({
      key: `${itemId}/list-edit`,
      Icon: Edit2,
      label,
      onClick: () => navigate(`/coupons/${itemId}?mode=edit&step=5`),
      disabled: isCouponsActionsFetching,
    });
  };

  const renderListDeleteButton = (itemId, couponTitle) => {
    const label = intl.formatMessage(messages.deleteCouponButtonLabel);
    return renderListActionButton({
      key: `${itemId}/list-delete`,
      Icon: Trash2,
      label,
      onClick: () => {
        openCouponActionModal();
        setDeleteCouponData({ id: itemId, title: couponTitle });
      },
      disabled: isCouponsActionsFetching,
      variant: 'delete',
    });
  };

  const renderListRepublishButton = itemId => {
    const label = intl.formatMessage(messages.republishCouponButtonLabel);
    return renderListActionButton({
      key: `${itemId}/list-republish`,
      Icon: RefreshCw,
      label,
      onClick: () => {
        setRepublishCouponId(itemId);
        openCouponActionModal();
      },
      disabled: isCouponsActionsFetching,
    });
  };

  const renderListSendNotificationButton = (itemId, couponTitle, coupon) => {
    if (followersCount === 0) return null;
    const label = intl.formatMessage(messages.sendNotificationsCouponButtonLabel);
    const isDisabled = !coupon?.notification_available || isCouponsActionsFetching;
    const disabledTitle = !coupon?.notification_available
      ? intl.formatMessage(messages.sendNotificationsTooltipText)
      : undefined;
    return renderListActionButton({
      key: `${itemId}/list-notify`,
      Icon: Bell,
      label,
      onClick: () => {
        openCouponActionModal();
        setSendNotificationCouponData({ couponId: itemId, coupon, businessInfo });
      },
      disabled: isDisabled,
      disabledTitle,
    });
  };

  const couponStateToListActionsMap = {
    sponsored: [renderListDeleteButton],
    published: [renderListEditButton, renderListDeleteButton, renderListSendNotificationButton],
    draft: [renderListPublishButton, renderListEditButton, renderListDeleteButton],
    expired: [renderListRepublishButton, renderListDeleteButton],
    blocked: [renderListDeleteButton],
  };

  const getListCouponActionButtons = item => {
    switch (true) {
      case item.is_blocked:
        return couponStateToListActionsMap.blocked;
      case item.is_sponsored:
        return couponStateToListActionsMap.sponsored;
      case item.state === COUPON_STATES.PUBLISHED:
        return couponStateToListActionsMap.published;
      case item.state === COUPON_STATES.DRAFT:
        return couponStateToListActionsMap.draft;
      case item.state === COUPON_STATES.EXPIRED:
        return couponStateToListActionsMap.expired;
      default:
        return null;
    }
  };

  const [filterStatus, setFilterStatus] = useState(COUPON_STATES.PUBLISHED);
  const filteredCoupons = couponsList.filter(
    coupon => filterStatus === 'all' || coupon.state === filterStatus,
  );

  // Grid/List view toggle for the coupons below — list is desktop-only
  // (hidden + forced back to a stacked layout under the sm breakpoint via
  // CSS, see MyCoupons.module.scss), same idea as the grid/list toggle on
  // the business-facing deals pages.
  const [viewMode, setViewMode] = useState('list');
  useEffect(() => {
    setExpandedCouponId(null);
  }, [viewMode]);

  return (
    <AppWrapper showHeader showSideNav showHeadingRow headingText={intl.formatMessage(messages.pageHeadingText)}>
      <AuthProtected />
      <div styleName="page">
        <div styleName="card-surface">
          <div styleName="page-body">
            {/* ─── Left column: business identity ───────────────────────
                BusinessCard is the same hero (cover photo, logo, name,
                email) MyBusinessAccount.js renders — reused, not rebuilt.
                The info card below it is this page's own read-only summary
                (Edit routes to /account, where BusinessInfoEditForm already
                handles changing these fields). */}
            <div styleName="left-col">
              <BusinessCard businessInfo={businessInfo} data={businessInfo} />

              <div styleName="info-card">
                <div styleName="info-card-header">
                  <h3 styleName="info-card-title">Business Info</h3>
                  <button
                    type="button"
                    styleName="info-card-edit"
                    onClick={() => setBusinessEditModalOpen(true)}
                    title="Edit business info"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>

                <div styleName="info-row">
                  <span styleName="info-label">Business Name</span>
                  <span styleName="info-value">{businessInfo?.business_name || businessInfo?.name || '—'}</span>
                </div>
                <div styleName="info-row">
                  <span styleName="info-label">Contact Name</span>
                  <span styleName="info-value">{businessInfo?.contact_name || businessInfo?.first_name || '—'}</span>
                </div>
                <div styleName="info-row">
                  <span styleName="info-label">Phone</span>
                  <span styleName="info-value">{businessInfo?.phone || '—'}</span>
                </div>
                <div styleName="info-row">
                  <span styleName="info-label">Street Address</span>
                  <span styleName="info-value">{businessInfo?.street || businessInfo?.address || '—'}</span>
                </div>
                <div styleName="info-row">
                  <span styleName="info-label">City</span>
                  <span styleName="info-value">{getLocationLabel(businessInfo?.city) || '—'}</span>
                </div>
                <div styleName="info-row">
                  <span styleName="info-label">Province</span>
                  <span styleName="info-value">{getLocationLabel(businessInfo?.province) || '—'}</span>
                </div>
                <div styleName={cx('info-row', 'info-row-last')}>
                  <span styleName="info-label">Postal Code</span>
                  <span styleName="info-value">{businessInfo?.postal_code || '—'}</span>
                </div>
              </div>
            </div>

            {/* ─── Right column: coupons (unchanged behaviour, existing
                filter-pills + the new Grid/List toggle) ───────────────── */}
            <div styleName="right-col">
              <div styleName="page-header-row">
                <h5 styleName="preview-label">
                  <FormattedMessage {...messages.couponPreviewLabel} />
                </h5>

                <div styleName="view-toggle">
                  <button
                    type="button"
                    styleName={cx('view-toggle-btn', { active: viewMode === 'grid' })}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid size={14} /> Grid
                  </button>
                  <button
                    type="button"
                    styleName={cx('view-toggle-btn', { active: viewMode === 'list' })}
                    onClick={() => setViewMode('list')}
                  >
                    <List size={14} /> List
                  </button>
                </div>
              </div>

              {businessInfo?.verified === false && (
                <div styleName="verify-banner">
                  <span styleName="verify-banner-icon">⏳</span>
                  <div>
                    <h4 styleName="verify-banner-title">Your account is currently under review</h4>
                    <p styleName="verify-banner-text">
                      Your coupons will automatically go live once your business is verified.
                      This typically takes 1-2 business days. We&apos;ll notify you via email
                      when your account is approved.
                    </p>
                  </div>
                </div>
              )}

              {initialLoadDone && !isCouponsFetching && verifiedBusiness !== false && (
                <div styleName="filter-pills">
                  {FILTER_OPTIONS.map(opt => (
                    <button
                      type="button"
                      key={opt.value}
                      styleName={cx('filter-pill', { active: filterStatus === opt.value })}
                      onClick={() => setFilterStatus(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {verifiedBusiness !== false && initialLoadDone && (
                <TransitionGroup styleName={cx('coupons-grid', { list: viewMode === 'list' })}>
                  {filteredCoupons.map(item => {
                    const isActionExecutedOnCurrentItem = item.id === coupons.couponsActions.couponId;
                    const isExpanded = expandedCouponId === item.id;

                    // Grid tucks this away in a collapsible dropdown —
                    // plain circular icon buttons.
                    const actionsContent = (
                      <>
                        <div styleName="analytics-row">
                          {renderAnalyticsData(viewCounts[item.id] || 0, 'viewsText')}
                          {renderAnalyticsData(item.save_coupon_count, 'likesText')}
                        </div>
                        <div styleName="action-buttons-pill-wrap">
                          {(getCouponActionButtons(item) || []).map(buttonRenderMethod => buttonRenderMethod(item.id, item.title, item))}
                        </div>
                      </>
                    );

                    // List renders inline in the card's own footer row via
                    // actionsNode — pill-shaped CTAs matching the Redeem
                    // button style instead of the grid's circular icons.
                    const listActionsContent = (
                      <div styleName="list-actions-row">
                        <div styleName="analytics-row">
                          {renderAnalyticsData(viewCounts[item.id] || 0, 'viewsText')}
                          {renderAnalyticsData(item.save_coupon_count, 'likesText')}
                        </div>
                        <div styleName="list-cta-group">
                          {(getListCouponActionButtons(item) || []).map(buttonRenderMethod => buttonRenderMethod(item.id, item.title, item))}
                        </div>
                      </div>
                    );

                    return (
                      <CSSTransition key={item.id} timeout={DEFAULT_TRANSITION_TIMEOUT} classNames="coupon-list-animation">
                        {viewMode === 'list' ? (
                          <div styleName="coupon-card list">
                            <Coupon
                              data={item}
                              businessData={businessInfo}
                              layout="list"
                              actionsNode={listActionsContent}
                            />

                            <Spinner
                              containerClassName="ml-2"
                              justify="start"
                              size="sm"
                              show={isCouponsActionsFetching && isActionExecutedOnCurrentItem}
                            />
                            <Feedback
                              invalid
                              show={!!(couponsActionsError && !isCouponsActionsFetching && isActionExecutedOnCurrentItem)}
                              content={couponsActionsError}
                            />
                          </div>
                        ) : (
                          <div styleName="coupon-card">
                            <div styleName="coupon-card-category">
                              <span styleName="category-label">
                                <FormattedMessage {...messages.categoryLabel} />
                              </span>
                              <span styleName="category-value">{item.category?.name}</span>
                            </div>

                            {/* Clicking the coupon image/media toggles the
                                Views/Likes + Edit/Delete row open as a
                                dropdown from the bottom of the card — it's
                                hidden by default so the grid stays clean. */}
                            <div
                              styleName="coupon-card-media"
                              role="button"
                              tabIndex={0}
                              aria-expanded={isExpanded}
                              onClick={() => toggleExpandedCoupon(item.id)}
                              onKeyPress={e => {
                                if (e.key === 'Enter') toggleExpandedCoupon(item.id);
                              }}
                            >
                              <Coupon data={item} businessData={businessInfo} squareImages />
                            </div>

                            <div styleName={cx('coupon-card-actions-collapse', { expanded: isExpanded })}>
                              <div styleName="coupon-card-actions">{actionsContent}</div>
                            </div>

                            <Spinner
                              containerClassName="ml-2"
                              justify="start"
                              size="sm"
                              show={isCouponsActionsFetching && isActionExecutedOnCurrentItem}
                            />
                            <Feedback
                              invalid
                              show={!!(couponsActionsError && !isCouponsActionsFetching && isActionExecutedOnCurrentItem)}
                              content={couponsActionsError}
                            />
                          </div>
                        )}
                      </CSSTransition>
                    );
                  })}
                </TransitionGroup>
              )}

              {verifiedBusiness !== false && !initialLoadDone && (
                <Spinner containerClassName="mt-5 mb-5" justify="center" size="lg" color="danger" show />
              )}

              {initialLoadDone && (isCouponsFetching || (couponsFetchingError && !hasLoadedCoupons)) && verifiedBusiness !== false && (
                <Skeleton
                  className="mt-5"
                  height={DEFAULT_NEXT_PAGE_LOADING_SKELETON_HEIGHT}
                  duration={DEFAULT_NEXT_PAGE_LOADING_SKELETON_DURATION}
                />
              )}

              {initialLoadDone && !isCouponsFetching && verifiedBusiness !== false && filteredCoupons.length === 0 && !couponsFetchingError && (
                <div styleName="state-message">No coupons found.</div>
              )}

              {verifiedBusiness !== false && hasLoadedCoupons && (
                <Feedback show={couponsFetchingError} invalid content={couponsFetchingError} />
              )}
            </div>
          </div>
        </div>

        {republishCouponId && couponActionModalOpen && (
          <CouponRepublish
            isOpen={couponActionModalOpen}
            republishCouponId={republishCouponId}
            closeRepublishCouponHandler={closeCouponActionModal}
          />
        )}
        {deleteCouponData && couponActionModalOpen && (
          <CouponDelete
            isOpen={couponActionModalOpen}
            deleteCouponData={deleteCouponData}
            closeModalHandler={closeCouponActionModal}
          />
        )}
        {sendNotificationCouponData && couponActionModalOpen && (
          <SendCouponNotificationModal
            isOpen={couponActionModalOpen}
            onClose={closeCouponActionModal}
            onConfirm={sendNotificationPaymentHandler}
            productCode={products.PUSH_NOTIFICATIONS_PROD_CODE}
            message="Would you like to send a push notification to your followers about this coupon?"
            couponNotificationData={sendNotificationCouponData}
          />
        )}

        <BusinessInfoEditModal
          isOpen={businessEditModalOpen}
          businessInfo={businessInfo}
          onClose={() => setBusinessEditModalOpen(false)}
          onSave={handleBusinessInfoSave}
          saving={businessInfoSaving}
        />
      </div>
    </AppWrapper>
  );
};

MyCoupons.propTypes = {
  intl: PropTypes.object,
  dispatch: PropTypes.func,
  coupons: PropTypes.object,
  businessInfo: PropTypes.object,
  viewCounts: PropTypes.object,
  followersCount: PropTypes.number,
};

const mapStateToProps = state => ({
  coupons: state.coupons,
  businessInfo: state.businessInfo.data,
  viewCounts: state.coupons.viewCounts || {},
  followersCount: state.followers.data?.followers_count,
});

export default connect(mapStateToProps)(injectIntl(CSSModule(MyCoupons, styles, { allowMultiple: true })));